import React from 'react';
import { Link } from 'react-router-dom';

// Import PDF files
import corporateFinancing from '../../assets/accessibility-files/02_Corporate-Financing-and-Investment-Decisions-When-Firms-Have-Information-That-Investors-Do-Not-Have-Myers-and-Majluf.pdf';
import aghionBolton from '../../assets/accessibility-files/Aghion Bolton 1992 Incomplete Contracting Approach to Financial Contracts.pdf';
import arnoldGrowth from '../../assets/accessibility-files/Arnold with Growth.pdf';
import carlsonSchool from '../../assets/accessibility-files/carlson-school-ms-finance-research-viewbook.pdf';
import formSheet from '../../assets/accessibility-files/Form Sheet Final.pdf';
import realOptions from '../../assets/accessibility-files/J Applied Corp Finance - 2022 - Myers - Real Options and Hidden Leverage.pdf';

const AccessibilityAgentPage = () => {
    const concerns = [
        {
            text: "Professors don't want to spend time, LMS teams can't do the work on ongoing basis",
            icon: "❌"
        },
        {
            text: "A single department accumulated over 2000 PDF docs. It's overwhelming.",
            icon: "❌"
        },
        {
            text: "Many PDFs the source documents aren't even available.",
            icon: "❌"
        },
        {
            text: "If we get caught in an opportunistic class action lawsuit, it will be very expensive.",
            icon: "❌"
        }
    ];

    const solutions = [
        {
            text: "PDF documents can be converted in-place, in the same folders",
            icon: "✅"
        },
        {
            text: "Documents can be converted in bulk and changed automatically.",
            icon: "✅"
        },
        {
            text: "Original source not required. We can update existing documents OR scan and rebuild them to be accessible.",
            icon: "✅"
        },
        {
            text: "We can show reasonable efforts to update and comply to all applicable documents.",
            icon: "✅"
        }
    ];

    const demoFiles = [
        {
            name: "Corporate Financing & Investment Decisions",
            description: "Myers and Majluf research paper - Complex academic document",
            file: corporateFinancing,
            type: "PDF"
        },
        {
            name: "Incomplete Contracting Approach",
            description: "Aghion Bolton 1992 - Financial contracts research paper",
            file: aghionBolton,
            type: "PDF"
        },
        {
            name: "Arnold with Growth",
            description: "Academic research document with growth analysis",
            file: arnoldGrowth,
            type: "PDF"
        },
        {
            name: "Carlson School MS Finance Research",
            description: "Research viewbook with complex layout and images",
            file: carlsonSchool,
            type: "PDF"
        },
        {
            name: "Form Sheet Final",
            description: "Structured form document with fillable fields",
            file: formSheet,
            type: "PDF"
        },
        {
            name: "Real Options and Hidden Leverage",
            description: "Journal of Applied Corporate Finance - Myers 2022",
            file: realOptions,
            type: "PDF"
        }
    ];

    return (
        <div className="min-h-screen bg-purple-50 pb-20">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
                {/* Header */}
                <div className="text-center mb-8 sm:mb-12">
                    <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                        The Accessibility Challenge
                    </h1>
                    <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
                        What's preventing you from being compliant with the new accessibility standards,
                        <strong> WCAG 2.1 AA</strong>, before April 24 2026?
                    </p>

                    <div className="mt-6 mb-4">
                        <a 
                            href="https://app.campusmind.ai/accessibility" 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-base sm:text-lg"
                        >
                            Try Accessibility Agent →
                        </a>
                    </div>
                </div>

                {/* Key Features */}
                <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8 mb-6 sm:mb-8">
                    <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                        What Makes Documents Accessible?
                    </h3>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
                        <div className="text-center p-4">
                            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Proper Structure</h4>
                            <p className="text-xs sm:text-sm text-gray-600">Tagged headings, lists, and reading order</p>
                        </div>

                        <div className="text-center p-3 sm:p-4">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Alt Text</h4>
                            <p className="text-xs sm:text-sm text-gray-600">Descriptive text for all images and graphics</p>
                        </div>

                        <div className="text-center p-3 sm:p-4">
                            <div className="w-12 h-12 sm:w-16 sm:h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                                <svg className="w-6 h-6 sm:w-8 sm:h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                </svg>
                            </div>
                            <h4 className="font-semibold text-gray-900 mb-1 sm:mb-2 text-sm sm:text-base">Text Recognition</h4>
                            <p className="text-xs sm:text-sm text-gray-600">OCR for scanned documents, selectable text</p>
                        </div>
                    </div>
                </div>

                {/* Concerns vs Solutions Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12">
                    {/* Concerns Section */}
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8 border-t-4 border-red-500">
                        <h2 className="text-xl sm:text-2xl font-bold text-red-600 mb-4 sm:mb-6 flex items-center gap-2">
                            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                            Concerns
                        </h2>
                        <div className="space-y-3 sm:space-y-4">
                            {concerns.map((concern, index) => (
                                <div key={index} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                                    <span className="text-xl sm:text-2xl flex-shrink-0">{concern.icon}</span>
                                    <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">{concern.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Solutions Section */}
                    <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 md:p-8 border-t-4 border-green-500">
                        <h2 className="text-xl sm:text-2xl font-bold text-green-600 mb-4 sm:mb-6 flex items-center gap-2">
                            <svg className="w-6 h-6 sm:w-8 sm:h-8" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            CampusMind Solutions
                        </h2>
                        <div className="space-y-3 sm:space-y-4">
                            {solutions.map((solution, index) => (
                                <div key={index} className="flex items-start gap-2 sm:gap-3 p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                                    <span className="text-xl sm:text-2xl flex-shrink-0">{solution.icon}</span>
                                    <p className="text-gray-700 text-xs sm:text-sm leading-relaxed">{solution.text}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Demo Files Section */}
                <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-xl p-6 sm:p-8 text-white mb-6 sm:mb-8">
                    <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 sm:mb-4 text-center">
                        Try the Accessibility Agent
                    </h2>
                    <p className="text-center text-purple-100 mb-6 sm:mb-8 text-sm sm:text-base md:text-lg px-2">
                        Experience how CampusMind can automatically convert your documents to be WCAG 2.1 AA compliant
                    </p>

                    {/* Demo Files Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                        {demoFiles.map((file, index) => (
                            <div key={index} className="bg-white rounded-lg p-3 hover:shadow-lg transition-all duration-200">
                                <div className="flex items-start gap-2 mb-2">
                                    <div className="w-8 h-8 bg-red-100 rounded flex items-center justify-center flex-shrink-0">
                                        <svg className="w-4 h-4 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                                            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <h4 className="font-semibold text-gray-900 text-xs mb-1 line-clamp-2">{file.name}</h4>
                                        <p className="text-xs text-gray-500">{file.type}</p>
                                    </div>
                                </div>
                                <a
                                    href={file.file}
                                    download={file.name + '.pdf'}
                                    className="w-full bg-purple-700 hover:bg-purple-800 text-white px-3 py-2 rounded text-xs font-medium transition-colors duration-200 flex items-center justify-center gap-1.5"
                                >
                                    <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </a>
                            </div>
                        ))}
                    </div>

                    {/* Info Box */}
                    <div className="mt-4 bg-purple-900 bg-opacity-50 border border-purple-300 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-purple-200 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                            </svg>
                            <div>
                                <h4 className="font-semibold text-white mb-1 text-sm">How to Use</h4>
                                <p className="text-xs text-purple-100">
                                    Download these sample PDFs to test the CampusMind Accessibility Agent. The system will automatically detect issues and generate WCAG 2.1 AA compliant versions.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation Buttons */}
                <div className="flex justify-center gap-3 sm:gap-4 md:gap-8 flex-wrap px-4">
                    <Link
                        to="/create-agent"
                        className="bg-gray-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-gray-700 transition-all duration-200 text-sm sm:text-base shadow-md hover:shadow-lg"
                    >
                        Back: Create Agent
                    </Link>
                    <Link
                        to="/it-helpdesk-agent"
                        className="bg-purple-700 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium hover:bg-purple-800 transition-all duration-200 text-sm sm:text-base shadow-md hover:shadow-lg"
                    >
                        Next: IT Workforce
                    </Link>
                </div>
            </div>
        </div>
    );
};

export default AccessibilityAgentPage;
